import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DayField } from "@/components/DayField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, fmtCr } from "@/lib/api";

const num = (v) => (v === "" ? null : Number(v));
const today = () => new Date().toISOString().slice(0, 10);
const BLANK = {
  kpi_name: "",
  kpi_unit: "",
  target_kpi: "",
  baseline_kpi: "",
  target_impact_lakh: "",
  date: today(),
  pct: "",
  actual_value: "",
  impact_lakh: "",
  remark: "",
};

/**
 * Put a usecase on compliance watch.
 *
 * Only a usecase from the hot trial onwards is offered — a cold trial is still a laboratory and a
 * usecase taken out of use has nothing left to comply with. The three settle-once figures are
 * asked for here so the odometer has a scale from its very first day.
 */
export const AddWatch = ({ plant, onAdded, testId = "add-watch" }) => {
  const [rows, setRows] = useState(null);
  const [chosen, setChosen] = useState("");
  const [form, setForm] = useState(BLANK);
  const [noReading, setNoReading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/compliance/eligible", { params: { plant } })
      .then((r) => setRows(r.data.rows))
      .catch((e) => {
        toast.error(apiError(e));
        setRows([]);
      });
  }, [plant]);

  const pick = (id) => {
    setChosen(id);
    const row = (rows || []).find((r) => r.id === id);
    setForm({
      ...BLANK,
      kpi_name: row?.kpi_name || "",
      kpi_unit: row?.kpi_unit || "",
    });
  };

  const add = async () => {
    if (!chosen) {
      toast.error("Pick the usecase first.");
      return;
    }
    if (!form.kpi_name.trim()) {
      toast.error("Name the KPI this usecase will be measured on.");
      return;
    }
    const reading = [form.pct, form.actual_value, form.impact_lakh].some((v) => v !== "");
    if (!noReading && !reading) {
      toast.error(
        "Give the first day's reading — the compliance per cent, the KPI as it read, or the impact. Tick the box below if there is nothing to record yet.",
      );
      return;
    }
    if (!noReading && reading && !form.date) {
      toast.error("Which day is that first reading for?");
      return;
    }
    setBusy(true);
    try {
      await api.post("/compliance/profiles", {
        project_id: chosen,
        kpi_name: form.kpi_name,
        kpi_unit: form.kpi_unit,
        target_kpi: num(form.target_kpi),
        baseline_kpi: num(form.baseline_kpi),
        target_impact_lakh: num(form.target_impact_lakh),
        visible: true,
      });
      if (!noReading && reading) {
        await api.post(`/projects/${chosen}/compliance`, {
          date: form.date,
          pct: num(form.pct),
          actual_value: num(form.actual_value),
          impact_lakh: num(form.impact_lakh),
          remark: form.remark || null,
        });
      }
      toast.success(
        noReading || !reading
          ? "Added. Its odometer is on the Dashboard — fill the first day when you have it."
          : `Added, with ${form.date} as its first day on record.`,
      );
      setChosen("");
      setForm(BLANK);
      setNoReading(false);
      onAdded?.();
      api.get("/compliance/eligible", { params: { plant } }).then((r) => setRows(r.data.rows));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!rows)
    return (
      <div className="flex h-20 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  const fields = [
    ["kpi_name", "KPI watched *", "text", "Specific fuel consumption"],
    ["kpi_unit", "Unit of measure", "text", "kg/thm"],
    ["target_kpi", "Target KPI", "number", "what was promised"],
    ["baseline_kpi", "Baseline KPI", "number", "where it stood before"],
    ["target_impact_lakh", "Target Impact (₹ Lakh)", "number", "in lakh"],
  ];

  return (
    <div data-testid={testId}>
      {!rows.length ? (
        <p className="text-sm text-muted-foreground">
          Nothing is waiting to go on watch in this view — every eligible usecase is already
          tracked. Compliance begins at the hot trial, so move a usecase to Hot Trials, Go-Live or
          beyond on the register and it will show up here.
        </p>
      ) : (
        <>
          <label className="block">
            <span className="label-caps block">Which usecase</span>
            <Select value={chosen} onValueChange={pick}>
              <SelectTrigger
                className="mt-1 h-11 rounded-none"
                data-testid={`${testId}-usecase`}
              >
                <SelectValue placeholder={`${rows.length} usecase(s) eligible — pick one`} />
              </SelectTrigger>
              <SelectContent className="max-h-80 rounded-none bg-popover">
                {rows.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`${testId}-option-${r.code}`}>
                    {r.plant} · {r.code} — {r.title} ({r.phase}
                    {r.expected_cr ? `, ${fmtCr(r.expected_cr)}` : ""})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {fields.map(([key, label, type, hint]) => (
              <label key={key} className="block">
                <span className="label-caps block">{label}</span>
                <Input
                  type={type}
                  step="any"
                  data-testid={`${testId}-${key}`}
                  value={form[key]}
                  placeholder={hint}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="mt-1 h-10 rounded-none"
                />
              </label>
            ))}
          </div>

          <div className="mt-4 border border-border bg-muted/30 p-3" data-testid={`${testId}-first-day`}>
            <div className="label-caps">
              The first reading · and the day it is for
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <label className="block">
                <span className="font-data block text-[10px] text-muted-foreground">
                  Date of this reading
                </span>
                <DayField
                  className="mt-1"
                  testId={`${testId}-date`}
                  format="iso"
                  max={today()}
                  disabled={noReading}
                  value={form.date}
                  onChange={(v) => setForm((f) => ({ ...f, date: v }))}
                />
              </label>
              {[
                ["pct", "Compliance %", "92"],
                ["actual_value", `KPI actual${form.kpi_unit ? ` (${form.kpi_unit})` : ""}`, "556.2"],
                ["impact_lakh", "Impact (₹ Lakh)", "19.0"],
                ["remark", "Remark", "shift average, stoppage, anything worth recording"],
              ].map(([key, label, hint]) => (
                <label key={key} className="block">
                  <span className="font-data block text-[10px] text-muted-foreground">{label}</span>
                  <Input
                    type={key === "remark" ? "text" : "number"}
                    step="any"
                    data-testid={`${testId}-${key}`}
                    value={form[key]}
                    placeholder={hint}
                    disabled={noReading}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="mt-1 h-10 rounded-none bg-card"
                  />
                </label>
              ))}
            </div>
            <label className="mt-3 flex items-center gap-2 text-[11px]">
              <input
                type="checkbox"
                data-testid={`${testId}-no-reading`}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={noReading}
                onChange={(e) => setNoReading(e.target.checked)}
              />
              There is no reading to record yet — I will fill the days later.
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid={`${testId}-submit`}
              disabled={busy || !chosen}
              onClick={add}
              className="flex h-11 items-center gap-2 bg-primary px-5 font-display text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add this usecase
            </button>
            <p className="max-w-xl text-[11px] text-muted-foreground">
              A target you do not have yet can be left blank — the odometer reads <b>N/A</b> for it
              and the usecase stands on the Data Inconsistency screen until it is declared.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
